import { useState } from "react";
import { useNavigate } from "react-router-dom";
import type { PhaseTaps, PrimaryWorkAction } from "../../../features/work-session";
import { useSuccessDialog } from "../../../shared/hooks/useSuccessDialog";
import { PATHS } from "../../../app/routes/paths";

export type AppointmentFlow = "complete" | "end-departure" | "follow-up" | "plan" | "reschedule" | null;

type Args = {
  taps: PhaseTaps;
  /** Only read once a session-close modal actually saves — undefined on the loading render is fine. */
  workOrderId: string | undefined;
};

/**
 * Every modal / overlay / success-dialog transition on the appointment page, collapsed onto one
 * `flow` value instead of the ten separate `useState` booleans this replaced. The four booleans
 * that drove the "save → success → optional Folgetermin planen → plan overlay → success →
 * navigate" chain are now one `useSuccessDialog`, extended with a `secondaryAction`.
 */
export function useAppointmentFlows({ taps, workOrderId }: Args) {
  const navigate = useNavigate();
  const [flow, setFlow] = useState<AppointmentFlow>(null);
  const { showSuccess, dialogProps, dismiss } = useSuccessDialog();

  const close = () => setFlow(null);

  /** Opening actions are pure taps; closing ones open their sheet. */
  const onPrimaryAction = (action: PrimaryWorkAction) => {
    if (action === "complete") setFlow("complete");
    else if (action === "end-departure") setFlow("end-departure");
    else taps.run(action);
  };

  const onFollowUp = () => setFlow("follow-up");
  const onReschedule = () => setFlow("reschedule");

  /** The three work-session modals (complete / end-departure / follow-up) land here on save. */
  const onSessionSaved = ({ followUp }: { followUp: boolean }) => {
    close();
    showSuccess("Zeiterfassung wurde erfolgreich gespeichert", {
      secondaryAction: followUp && workOrderId ? {
        label: "Folgetermin planen",
        onClick: () => {
          dismiss();
          setFlow("plan");
        },
      } : undefined,
    });
  };

  const onPlanned = () => {
    close();
    showSuccess("Folgetermin erfolgreich geplant", () => navigate(PATHS.workerCalendar));
  };

  const onRescheduled = () => {
    close();
    showSuccess("Termin erfolgreich umgeplant", () => navigate(PATHS.workerCalendar));
  };

  return {
    flow,
    close,
    onPrimaryAction,
    onFollowUp,
    onReschedule,
    onSessionSaved,
    onPlanned,
    onRescheduled,
    success: dialogProps,
  };
}

export type AppointmentFlows = ReturnType<typeof useAppointmentFlows>;
